/**
 * SSO Bridge landing page - Phase A, doc 21 §3.2.
 *
 * Lands at `<slug>.discovery.foctta.com/auth/bridge` when the IT Admin
 * clicks "Open FOCTTA Discovery" from inside Core's tenant SPA. The
 * Core SPA navigates here with the bridge token in the URL fragment:
 *
 *   https://customer-oswal.discovery.foctta.com/auth/bridge#t=<jwt>
 *
 * Reads the token from `window.location.hash`, POSTs to /v1/auth/exchange,
 * persists the returned Discovery session via setAuth(), wipes the
 * fragment so the token never appears in history / Referer / share
 * sheets, and redirects to /onboarding (or `?next=` if Core provided one).
 *
 * Failure UX:
 *   - Token missing / malformed → "This link is incomplete or has
 *     already been used. Please return to FOCTTA Core and try again."
 *   - 401 / verifier failed     → "We couldn't verify your session.
 *     Ask your IT Admin to confirm FOCTTA Core is paired with this
 *     Discovery tenant."
 *   - Network error              → "Could not reach Discovery. Try again."
 *
 * No automatic retry - bridge tokens are 5-min, single-use. A second
 * exchange attempt with the same token would fail.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, ShieldAlert } from 'lucide-react';

import { Mark } from '@/components/brand/Mark';
import { exchangeBridgeToken, setAuth } from '@/lib/api';

type Status =
  | { state: 'reading' }
  | { state: 'exchanging' }
  | { state: 'success' }
  | { state: 'error'; title: string; detail: string };

/**
 * Extract the bridge token + optional `next` redirect from the URL
 * fragment. Format:
 *   #t=<base64url-jwt>&next=/some/path
 */
function parseBridgeFragment(hash: string): { token: string | null; next: string | null } {
  // hash starts with '#'. Trim that, then parse as URL-encoded query.
  const trimmed = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!trimmed) return { token: null, next: null };
  const params = new URLSearchParams(trimmed);
  const token = params.get('t');
  const next = params.get('next');
  // Sanitise next - must be a relative path starting with /, never an
  // absolute URL (open-redirect defence).
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
  return { token, next: safeNext };
}

export function AuthBridge() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>({ state: 'reading' });

  useEffect(() => {
    const { token, next } = parseBridgeFragment(window.location.hash);

    // Wipe the fragment immediately - defence in depth so the token
    // doesn't survive in browser history or `document.referrer`.
    if (window.location.hash) {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    }

    if (!token) {
      setStatus({
        state: 'error',
        title: 'Bridge link incomplete',
        detail:
          'This link is missing its sign-on token. It may have already been used, or your browser stripped it. Return to FOCTTA Core and click "Open FOCTTA Discovery" again.',
      });
      return;
    }

    let cancelled = false;
    setStatus({ state: 'exchanging' });
    exchangeBridgeToken(token)
      .then((res) => {
        if (cancelled) return;
        // The Consent deployment this session came from, for the top bar's
        // "FOCTTA Consent" link back (release 3.1, persona D6).
        setAuth(res.token, { ...res.user, ...(res.consentUrl ? { consentUrl: res.consentUrl } : {}) }, res.expiresAt);
        setStatus({ state: 'success' });
        // Defer the navigation to next tick so the success card flashes
        // briefly. Keeps the UX from feeling like a silent hop.
        setTimeout(() => {
          if (cancelled) return;
          // 2026-05-27 - bridge lands on Home (/) by default. The
          // `next` query param still wins so Core-issued bridges can
          // target deep links (e.g. /compliance/breaches). Welcome
          // is now a settings-area checklist, not the landing page.
          navigate({ to: next ?? '/', replace: true });
        }, 250);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setStatus({
          state: 'error',
          title: 'Could not sign you in',
          detail:
            message.includes('401') || message.toLowerCase().includes('unauthorized')
              ? 'FOCTTA Core is not paired with this Discovery tenant, or the sign-on token has expired. Ask your IT Admin to confirm the integration in Core, then try again.'
              : `Discovery returned an error while exchanging your sign-on token: ${message}`,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-6">
          <Mark className="h-12 w-12" />

          {status.state === 'reading' || status.state === 'exchanging' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
              <div>
                <div className="text-lg font-semibold">Signing you in to FOCTTA Discovery…</div>
                <div className="text-sm text-muted-foreground">
                  Verifying your session from FOCTTA Core.
                </div>
              </div>
            </div>
          ) : null}

          {status.state === 'success' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                You're in. Loading your dashboard…
              </div>
            </div>
          ) : null}

          {status.state === 'error' ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <ShieldAlert className="h-10 w-10 text-amber-500" aria-hidden />
              <div>
                <div className="text-lg font-semibold">{status.title}</div>
                <div className="mt-2 text-sm text-muted-foreground">{status.detail}</div>
              </div>
              <button
                type="button"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate({ to: '/' })}
              >
                Go to home
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default AuthBridge;
